import { FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Calendar } from "@components/ui/calendar";
import { Button } from "@components/ui/button";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { TrainingDateRow } from "../shared/training-date-row.content";
import { calculateDayHours, createDefaultDateSchedule, formatHoursDisplay } from "../shared/training-schedule-form.utils";
import { useTrainingSchedule } from "../shared/use-training-schedule";
import { BulkDayTemplate } from "../shared/bulk-day-template.content";

interface ExternalBatchRequestScheduleFormProps {
  initialStartDate: Date;
  now: Date;
  numberOfMonthsDisplayed: number;
  description?: {
    title: string;
    subtitle: string;
  };
}

export const ExternalBatchRequestScheduleForm = ({
  initialStartDate,
  now,
  numberOfMonthsDisplayed,
  description,
}: ExternalBatchRequestScheduleFormProps) => {
  const {
    control,
    fields,
    currentMonth,
    totalHours,
    selectedDates,
    sortedFieldsWithIndex,
    bankHolidaysSelected,
    onCalendarSelect,
    handleHalfDayChange,
    applyDayTemplateToAll,
    handleRemove,
    handleClearAll,
  } = useTrainingSchedule({
    initialStartDate,
    createSchedule: createDefaultDateSchedule,
  });

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col items-center space-y-4">
        <div className="space-y-2">
          <FormLabel>Demi-journées de formation</FormLabel>
          {description && (
            <div className="space-y-1">
              <p className="text-sm">{description.title}</p>
              <p className="text-sm text-muted-foreground">{description.subtitle}</p>
            </div>
          )}
        </div>

        {bankHolidaysSelected.length > 0 && (
          <div className="text-sm text-yellow-500">
            <p>
              Avertissement : Vous avez sélectionné {bankHolidaysSelected.length} jour{bankHolidaysSelected.length > 1 ? "s" : ""} férié
              {bankHolidaysSelected.length > 1 ? "s" : ""}. Veuillez vérifier que ces dates sont correctes et conformes au planning de la
              formation.
            </p>
            <ul className="list-disc pl-5">
              {bankHolidaysSelected.map(({ date }, index) => (
                <li key={index}>{format(date, "PPP", { locale: fr })}</li>
              ))}
            </ul>
          </div>
        )}

        <Calendar
          mode="multiple"
          selected={selectedDates}
          onSelect={dates => {
            if (dates) {
              onCalendarSelect(dates);
            }
          }}
          numberOfMonths={numberOfMonthsDisplayed}
          today={now}
          weekStartsOn={1}
          locale={fr}
          className="p-0"
          defaultMonth={currentMonth || undefined}
          showOutsideDays={false}
        />

        <div className="flex w-full justify-end">
          <Button type="button" onClick={handleClearAll} disabled={fields.length === 0}>
            Effacer tout
          </Button>
        </div>
      </div>

      <FormField
        control={control}
        name="trainingSchedules"
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />

      <BulkDayTemplate dayCount={fields.length} onApply={applyDayTemplateToAll} />

      <div className="flex flex-col space-y-3">
        {sortedFieldsWithIndex.map(({ field, originalIndex }, displayIndex) => (
          <FormField
            key={field.date.getTime()}
            control={control}
            name={`trainingSchedules.${originalIndex}`}
            render={() => (
              <FormItem>
                <TrainingDateRow
                  date={field.date}
                  index={displayIndex}
                  morning={field.morning}
                  afternoon={field.afternoon}
                  dayHours={calculateDayHours(field)}
                  onHalfDayChange={(halfDay, changes) => handleHalfDayChange(originalIndex, halfDay, changes)}
                  onRemove={() => handleRemove(originalIndex)}
                />
              </FormItem>
            )}
          />
        ))}
      </div>

      {fields.length > 0 && (
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/50 p-4">
            <p className="text-center text-lg font-semibold">
              Durée totale de la masterclass : <span className="text-destructive">{formatHoursDisplay(totalHours)}</span>
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            <strong>
              Merci de vérifier que le nombre total d'heures calculé correspond bien à celui indiqué sur le devis de la masterclass.
              Veuillez corriger si nécessaire afin d'assurer la concordance. Toute incohérence entraînera la nécessité de refaire la
              demande.
            </strong>
          </p>
        </div>
      )}
    </div>
  );
};
